import type { Layout, LayoutItem } from 'react-grid-layout';
import type { BlockId } from '../studio/types';
import { sortLayoutByReadingOrder } from './sourcePageLayout';

export interface ExpansionLayoutPlan {
  readonly toKeep: Layout;
  readonly stayBelow: Layout;
  readonly spillover: Layout;
}

export interface RightOrBelowSplit {
  readonly toMove: Layout;
  readonly toKeep: Layout;
}

/**
 * Splits a page's layout into blocks that should move to a spillover page
 * (those geometrically right-of or below the expanding block) and blocks
 * that should stay on the source page.
 *
 * - "Right of": block.x >= expanding.x + expanding.w AND y-range overlaps.
 * - "Below": block.y >= expanding.y + expanding.h.
 * - The expanding block itself is always kept on the source page.
 */
export const splitRightOrBelowNeighbors = ({
  layout,
  expandingBlockId,
}: {
  layout: Layout;
  expandingBlockId: BlockId;
}): RightOrBelowSplit => {
  const expanding = layout.find((l) => l.i === expandingBlockId);
  if (!expanding) return { toMove: [], toKeep: layout.slice() };

  const toMove: LayoutItem[] = [];
  const toKeep: LayoutItem[] = [];

  for (const block of layout) {
    if (block.i === expandingBlockId) {
      toKeep.push(block);
      continue;
    }

    const isBelow = block.y >= expanding.y + expanding.h;
    const yOverlaps = block.y < expanding.y + expanding.h && block.y + block.h > expanding.y;
    const isRight = block.x >= expanding.x + expanding.w && yOverlaps;

    if (isBelow || isRight) {
      toMove.push(block);
    } else {
      toKeep.push(block);
    }
  }

  return { toMove, toKeep };
};

/**
 * Extends `splitRightOrBelowNeighbors` with fit-aware packing.
 *
 * Each movable neighbor is evaluated in reading order (top-to-bottom, left-to-right).
 * A neighbor **stays on the source page** (repositioned immediately below the expanded
 * block's bottom edge) when it fits within the page row capacity. Neighbors that don't
 * fit are placed in `spillover` with their original positions intact so the caller can
 * normalise them onto a new page.
 *
 * `x` and `w` are always preserved; only `y` changes for blocks that stay.
 */
export const planNeighborsAfterExpansion = ({
  layout,
  expandingBlockId,
  expandedRows,
  maxRows,
}: {
  layout: Layout;
  expandingBlockId: BlockId;
  expandedRows: number;
  maxRows: number;
}): ExpansionLayoutPlan => {
  const { toMove, toKeep } = splitRightOrBelowNeighbors({ layout, expandingBlockId });
  const expanding = layout.find((l) => l.i === expandingBlockId);

  if (!expanding || toMove.length === 0) {
    return { toKeep, stayBelow: [], spillover: toMove.slice() };
  }

  const sorted = sortLayoutByReadingOrder(toMove);
  const stayBelow: LayoutItem[] = [];
  const spillover: LayoutItem[] = [];
  let runningY = expanding.y + expandedRows;

  for (const block of sorted) {
    if (runningY + block.h <= maxRows) {
      stayBelow.push({ ...block, y: runningY });
      runningY += block.h;
    } else {
      spillover.push(block);
    }
  }

  return { toKeep, stayBelow, spillover };
};
